/**
 * Session restore — puts an organizer back into the event they were
 * working in after a native cold start.
 *
 * The Capacitor WebView always boots at `/`: unlike a browser tab there
 * is no URL to preserve, so when iOS/Android kill the backgrounded app
 * (or Capgo applies an OTA bundle on relaunch) the user lands on the
 * events picker even though their token and cached data are still in
 * localStorage.
 *
 * How it works:
 *   - A router `onResolved` subscription (wired in main.tsx) maintains
 *     one snapshot under SNAPSHOT_KEY: { eventId, path, ts }.
 *       - Any `/events/$eventId/*` location updates eventId.
 *       - path is recorded only for RESTORABLE_PATH_RE surfaces. Editor
 *         flows (event-content create/edit) are never recorded, so dying
 *         mid-edit restores to the last stable surface visited in that
 *         event, or its dashboard if none was recorded.
 *       - The events picker (`/events`) CLEARS the snapshot — leaving
 *         the event on purpose isn't a session. Logout clears it too
 *         (auth-provider), so the next login starts clean.
 *   - On a native cold boot, the `/` route's beforeLoad asks
 *     getRestoreTarget() for a redirect once auth context confirms the
 *     user is signed in. The decision here is fully synchronous
 *     (localStorage only — venue wifi must never block boot); the
 *     `/events/$eventId` guard then re-verifies event access over the
 *     network exactly as it does for a manual navigation, and bounces
 *     to the picker if the event is gone or access was revoked. Any
 *     failed layer falls through to `/events`, i.e. the old behavior.
 *
 * There are no deep links in the studio today (@capacitor/app is not
 * installed). If they are ever added, call suppressSessionRestore()
 * before the router mounts when a launch URL is present — a deep-link
 * launch must own that boot. See eventpay-app's main.tsx for the
 * reference wiring (await App.getLaunchUrl(), not appUrlOpen, which is
 * not guaranteed to fire on cold starts).
 */

import { Capacitor } from '@capacitor/core'
import type { AnyRouter } from '@tanstack/react-router'

const SNAPSHOT_KEY = 'studio_session_snapshot'

// There is no synchronous liveness cache to consult (event data lives
// in React Query / Dexie, both async), so restore is bounded by
// snapshot age alone; the event-access re-check in the `$eventId` guard
// catches everything age can't. A week covers event-week workflows and
// the morning-after reporting session.
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000

// Stable browsing surfaces that are safe to restore exactly. reload and
// transfer are deliberately included: they are all-day operator
// stations, and pathname-only restore (no search params) lands them on
// a fresh page with no half-completed transaction state. Editor flows
// are intentionally absent, and new routes DO NOT restore until added
// here — an unlisted path is never recorded, so restore falls back to
// the event's last recorded surface or its dashboard.
const RESTORABLE_PATH_RE =
  /^\/events\/\d+\/(?:dashboard|event-content|financials|home-layout|integrations|live-stats|menu|orders|reload|reporting|settings|site-map|support|toolkit|transactions|transfer|users|vendors(?:\/\d+(?:\/locations\/\d+)?)?|vouchers)\/?$/

const EVENT_PATH_RE = /^\/events\/(\d+)(?:\/|$)/

// Being on the picker means the user left their event on purpose —
// relaunching must not bounce them back into it.
const CLEARING_PATH_RE = /^\/events\/?$/

interface SessionSnapshot {
  eventId: string
  path: string | null
  ts: number
}

let suppressed = false

/**
 * A deep-link launch owns this boot: skip restore entirely. Must be
 * called before the router mounts. Unused until the studio grows deep
 * links — see the module comment.
 */
export function suppressSessionRestore(): void {
  suppressed = true
}

/** Subscribe the snapshot recorder to router navigations. */
export function initSessionRestore(router: AnyRouter): void {
  router.subscribe('onResolved', ({ toLocation }) => {
    try {
      recordLocation(toLocation.pathname)
    } catch (err) {
      console.warn('[SessionRestore] Failed to record location:', err)
    }
  })
}

/**
 * Update the snapshot for a resolved location. Exported for tests;
 * production writes go through the initSessionRestore subscription.
 */
export function recordLocation(pathname: string): void {
  if (typeof window === 'undefined') return

  if (CLEARING_PATH_RE.test(pathname)) {
    localStorage.removeItem(SNAPSHOT_KEY)
    return
  }

  const eventMatch = pathname.match(EVENT_PATH_RE)
  // Non-event routes (auth, account, admin) leave the snapshot
  // untouched — the user hasn't left their event context.
  if (!eventMatch) return

  const eventId = eventMatch[1]
  const prev = readSnapshot()
  // A path recorded for a different event must not survive an event
  // switch (direct URL switches bypass the picker).
  const prevPath = prev?.eventId === eventId ? prev.path : null

  const snapshot: SessionSnapshot = {
    eventId,
    path: RESTORABLE_PATH_RE.test(pathname) ? pathname : prevPath,
    ts: Date.now(),
  }
  localStorage.setItem(SNAPSHOT_KEY, JSON.stringify(snapshot))
}

/** Logout must not leave a restore target for the next login to inherit. */
export function clearSessionSnapshot(): void {
  if (typeof window === 'undefined') return
  localStorage.removeItem(SNAPSHOT_KEY)
}

/**
 * Where a cold boot at `/` should land instead of the events picker,
 * or null to leave the default behavior alone. The caller (`/` route)
 * has already established that the user is authenticated.
 */
export function getRestoreTarget(): string | null {
  // Native-only: on web the browser preserves the URL itself, and
  // navigating to `/` deliberately should land on the picker.
  if (!Capacitor.isNativePlatform()) return null
  if (suppressed) return null

  const snapshot = readSnapshot()
  if (!snapshot) return null

  if (Date.now() - snapshot.ts > MAX_AGE_MS) {
    console.log('[SessionRestore] Snapshot too old — not restoring')
    return null
  }

  const target =
    snapshot.path && RESTORABLE_PATH_RE.test(snapshot.path)
      ? snapshot.path
      : `/events/${snapshot.eventId}/dashboard`
  console.log('[SessionRestore] Restoring', target)
  return target
}

function readSnapshot(): SessionSnapshot | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = localStorage.getItem(SNAPSHOT_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<SessionSnapshot>
    // Validate shape defensively — the snapshot may predate an OTA
    // update that changed this module.
    if (typeof parsed.eventId !== 'string' || !/^\d+$/.test(parsed.eventId)) return null
    if (typeof parsed.ts !== 'number') return null
    const path =
      typeof parsed.path === 'string' && parsed.path.startsWith(`/events/${parsed.eventId}/`)
        ? parsed.path
        : null
    return { eventId: parsed.eventId, path, ts: parsed.ts }
  } catch {
    return null
  }
}
